<template>
  <BasePageCard>
    <div class="freight-table-create">
      <div class="freight-table-create__header">
        <h2 class="freight-table-create__title">Nova tabela de frete</h2>
      </div>

      <div v-if="loadingDraft" class="freight-table-create__loading">
        <a-spin />
      </div>

      <div v-else class="freight-table-create__sections">
        <!-- 1. Identificação -->
        <FreightTableFormSection
          :index="1"
          title="Identificação"
          subtitle="Configure as informações iniciais da tabela de frete"
          :open="openSection === 1"
          :done="isSectionDone(1)"
          :locked="isSectionLocked(1)"
          @toggle="toggleSection(1)"
          @locked-click="onLockedClick"
        >
          <FreightTableIdentificationSection
            :codigo.sync="form.identification.codigo"
            :descricao.sync="form.identification.descricao"
            :observacao.sync="form.identification.observacao"
            :tp-tabela.sync="form.configRoutes.tp_tabela"
            :tp-receita-despesa.sync="form.configRoutes.tp_receita_despesa"
            :tp-composicao.sync="form.configRoutes.tp_composicao"
          />
        </FreightTableFormSection>

        <!-- 2. Parceiro -->
        <FreightTableFormSection
          :index="2"
          title="Parceiro"
          subtitle="Informe a(s) transportadora(s) e o(s) cliente(s), caso seja um tipo de tabela receita, que irá ser composta."
          :open="openSection === 2"
          :done="isSectionDone(2)"
          :locked="isSectionLocked(2)"
          @toggle="toggleSection(2)"
          @locked-click="onLockedClick"
        >
          <div class="freight-table-create__partners">
            <FreightTableSupplierSection :value.sync="form.suppliers" />
            <!-- Trocar para despesa não descarta a seleção: o mapper só manda `pessoa` quando
                 o tipo é receita, então voltar o radio devolve os clientes já escolhidos. -->
            <FreightTableClientSelect
              v-if="isReceita"
              :value.sync="form.configRoutes.pessoa"
            />
          </div>
        </FreightTableFormSection>

        <!-- 3. Configurações e Rotas -->
        <FreightTableFormSection
          :index="3"
          title="Configurações e Rotas"
          subtitle="Cadastre o tipo de cálculo, vigência e valores negociados por rota"
          :open="openSection === 3"
          :done="isSectionDone(3)"
          :locked="isSectionLocked(3)"
          @toggle="toggleSection(3)"
          @locked-click="onLockedClick"
        >
          <FreightTableConfigRoutesSection v-model="form.configRoutes" />
        </FreightTableFormSection>
      </div>

      <div class="freight-table-create__actions-bar">
        <div class="freight-table-create__actions-group">
          <a-button
            class="freight-table-create__cancel"
            :loading="discarding"
            :disabled="saving || savingDraft"
            @click="onCancel"
            >Cancelar</a-button
          >
          <a-button
            :loading="savingDraft"
            :disabled="!canSaveDraft || saving || discarding"
            @click="onSaveDraft"
            >Salvar rascunho</a-button
          >
        </div>

        <div class="freight-table-create__actions-group">
          <a-button
            :disabled="
              !focusedSection ||
              focusedSection === 1 ||
              saving ||
              discarding ||
              savingDraft
            "
            @click="goToStep(focusedSection - 1)"
            >Voltar</a-button
          >
          <!-- span interno: botão desabilitado do antd não dispara mouseenter,
               então o tooltip precisa de um elemento habilitado por fora. -->
          <a-tooltip :title="primaryActionHint" placement="topRight">
            <span>
              <a-button
                type="primary"
                class="freight-table-create__save"
                :loading="saving"
                :disabled="!primaryActionEnabled || discarding || savingDraft"
                @click="onPrimaryAction"
              >
                {{ primaryActionLabel }}
              </a-button>
            </span>
          </a-tooltip>
        </div>
      </div>
    </div>
  </BasePageCard>
</template>

<script>
import { mapActions } from "vuex";
import BasePageCard from "@/shared/components/BasePageCard.vue";
import FreightTableFormSection from "@/modules/FreightTables/components/Create/FreightTableFormSection.vue";
import FreightTableIdentificationSection from "@/modules/FreightTables/components/Create/FreightTableIdentificationSection.vue";
import FreightTableSupplierSection from "@/modules/FreightTables/components/Create/FreightTableSupplierSection.vue";
import FreightTableClientSelect from "@/modules/FreightTables/components/Create/FreightTableClientSelect.vue";
import FreightTableConfigRoutesSection from "@/modules/FreightTables/components/Create/FreightTableConfigRoutesSection.vue";
import {
  createEmptyConfigRoutesForm,
  describeConfigRoutesGaps,
  isConfigRoutesValid,
} from "@/modules/FreightTables/services/freightTableConfigForm";
import { FREIGHT_TABLE_TIPO_RECEITA } from "@/modules/FreightTables/constants/freightTableOptions";
import { confirmDeleteDraft } from "@/modules/FreightTables/utils/confirmDeleteDraft";
import { describeStoreValidationErrors } from "@/modules/FreightTables/utils/describeStoreValidationErrors";
import { handleApiError } from "@/shared/utils/handleApiError";

const DRAFT_BASE_PATH = "/tabela-frete/rascunho";
const STEP_SLUGS = ["identificacao", "fornecedor", "configuracoes"];
const LAST_STEP = STEP_SLUGS.length;
const STEP_SAVE_ERROR = {
  1: "Não foi possível salvar a identificação da tabela de frete",
  2: "Não foi possível salvar os fornecedores da tabela de frete",
  3: "Não foi possível salvar o rascunho da tabela de frete",
};

export default {
  name: "FreightTableCreateView",
  components: {
    BasePageCard,
    FreightTableFormSection,
    FreightTableIdentificationSection,
    FreightTableSupplierSection,
    FreightTableClientSelect,
    FreightTableConfigRoutesSection,
  },
  data() {
    return {
      openSection: 1,
      // completedStep: maior etapa já concluída (monotônica; nunca regride ao
      // reeditar uma etapa anterior). 0 = nada concluído; a etapa N fica
      // destravada quando completedStep >= N - 1.
      completedStep: 0,
      saving: false,
      savingDraft: false,
      discarding: false,
      loadingDraft: false,
      tabfreId: null,
      form: {
        identification: {
          codigo: "",
          descricao: "",
          observacao: "",
        },
        suppliers: [],
        configRoutes: createEmptyConfigRoutesForm(),
      },
    };
  },
  computed: {
    identificationDone() {
      const { codigo, descricao } = this.form.identification;
      return (
        !!codigo.trim() &&
        !!descricao.trim() &&
        this.form.configRoutes.tp_receita_despesa != null
      );
    },
    // Cliente só existe na tabela receita — o backend exige `pessoa` quando o tipo é 1.
    isReceita() {
      return (
        Number(this.form.configRoutes.tp_receita_despesa) ===
        FREIGHT_TABLE_TIPO_RECEITA
      );
    },
    header() {
      const { tp_tabela, tp_receita_despesa, tp_composicao } =
        this.form.configRoutes;
      return { tp_tabela, tp_receita_despesa, tp_composicao };
    },
    partnersDone() {
      const hasSuppliers =
        Array.isArray(this.form.suppliers) && this.form.suppliers.length > 0;
      return (
        hasSuppliers &&
        (!this.isReceita || (this.form.configRoutes.pessoa || []).length > 0)
      );
    },
    configRoutesDone() {
      return isConfigRoutesValid(this.form.configRoutes);
    },
    // Botão age sobre o accordion aberto — não sobre a "próxima etapa pendente".
    focusedSection() {
      return this.openSection;
    },
    primaryActionLabel() {
      return this.focusedSection === LAST_STEP ? "Criar tabela" : "Avançar";
    },
    focusedSectionValid() {
      if (!this.focusedSection || this.saving) return false;
      if (this.focusedSection === 1) return this.identificationDone;
      if (this.focusedSection === 2) return this.partnersDone;
      return this.configRoutesDone;
    },
    primaryActionEnabled() {
      return this.focusedSectionValid;
    },
    // Quatro dos obrigatórios da etapa 3 ficam dentro das abas Carga e
    // Características: sem listar o que falta, o botão travado não se explica.
    primaryActionHint() {
      if (this.primaryActionEnabled || this.focusedSection !== LAST_STEP) {
        return "";
      }
      const pendencias = describeConfigRoutesGaps(this.form.configRoutes);
      return pendencias.length
        ? `Falta preencher — ${pendencias.join("; ")}`
        : "";
    },
    // Etapa 3 não tem persistência parcial: o rascunho já está salvo, então
    // "Salvar rascunho" apenas sai sem finalizar.
    canSaveDraft() {
      if (this.focusedSection === LAST_STEP) return !!this.tabfreId;
      return this.focusedSectionValid;
    },
    hasStoredSuppliers() {
      return this.completedStep >= 2;
    },
    // Maior etapa que o gating permite abrir.
    maxOpenableStep() {
      return Math.min(this.completedStep + 1, LAST_STEP);
    },
  },
  watch: {
    "$route.params.etapa": function () {
      if (this.tabfreId) this.syncStepFromRoute();
    },
  },
  created() {
    if (this.$route.params.id) this.loadDraft(this.$route.params.id);
    // Sem os selects preenchidos a etapa 3 não é submetível — o operador precisa
    // saber que a falha foi de carregamento, não de preenchimento.
    this.loadOptions();
  },
  methods: {
    ...mapActions("freightTables", [
      "createFreightTableDraft",
      "updateFreightTableDraft",
      "deleteFreightTableDraft",
      "storeFreightTableDraftSuppliers",
      "updateFreightTableDraftSuppliers",
      "fetchFreightTableDraft",
      "finalizeFreightTableDraft",
      "fetchFreightTableOptions",
      "fetchItineraryOptions",
      "saveFreightTableConfigRoutes",
    ]),
    async loadOptions() {
      try {
        await Promise.all([
          this.fetchFreightTableOptions(),
          this.fetchItineraryOptions(),
        ]);
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível carregar as opções da tabela de frete",
        );
      }
    },
    async loadDraft(tabfreId) {
      this.loadingDraft = true;
      try {
        const draft = await this.fetchFreightTableDraft({ tabfreId });

        if (!draft.isDraft) {
          this.$router.replace(`/tabela-frete/${tabfreId}`);
          return;
        }

        this.tabfreId = Number(tabfreId);
        this.form.identification = draft.identification;
        this.form.suppliers = draft.suppliers;
        Object.keys(draft.header).forEach((campo) => {
          if (draft.header[campo] != null) {
            this.form.configRoutes[campo] = draft.header[campo];
          }
        });
        this.completedStep = draft.suppliers.length ? 2 : 1;
        // Clientes não vão para o rascunho: um rascunho receita volta pela etapa Parceiro.
        if (this.completedStep === 2 && !this.partnersDone) {
          this.goToStep(2);
          return;
        }
        this.syncStepFromRoute();
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível carregar o rascunho da tabela de frete",
        );
        this.$router.replace("/tabela-frete");
      } finally {
        this.loadingDraft = false;
      }
    },
    stepPath(step) {
      if (!this.tabfreId) return DRAFT_BASE_PATH;
      return `${DRAFT_BASE_PATH}/${this.tabfreId}/${STEP_SLUGS[step - 1]}`;
    },
    syncStepFromRoute() {
      const requested = STEP_SLUGS.indexOf(this.$route.params.etapa) + 1;
      const allowed = requested >= 1 && requested <= this.maxOpenableStep;
      this.goToStep(allowed ? requested : this.maxOpenableStep);
    },
    goToStep(step) {
      this.openSection = step;
      const path = this.stepPath(step);
      if (path !== this.$route.path) this.$router.replace(path);
    },
    isSectionDone(index) {
      return this.completedStep >= index;
    },
    isSectionLocked(index) {
      return index > this.completedStep + 1;
    },
    toggleSection(index) {
      if (this.isSectionLocked(index)) return;
      if (this.openSection === index) {
        this.openSection = null;
        return;
      }
      this.goToStep(index);
    },
    onLockedClick() {
      this.$warning({
        title: "Etapa bloqueada",
        content: "Conclua a etapa anterior para liberar esta.",
        okText: "Entendi",
      });
    },
    // Grava a etapa aberta no rascunho. Etapa 3 não tem endpoint parcial.
    async persistStep() {
      if (this.focusedSection === 1) {
        if (this.tabfreId) {
          await this.updateFreightTableDraft({
            identification: this.form.identification,
            header: this.header,
            tabfreId: this.tabfreId,
          });
        } else {
          const created = await this.createFreightTableDraft({
            identification: this.form.identification,
            header: this.header,
          });
          this.tabfreId = created.tabfreId;
        }
        this.completedStep = Math.max(this.completedStep, 1);
        return;
      }
      if (this.focusedSection === 2) {
        const payload = {
          tabfreId: this.tabfreId,
          suppliers: this.form.suppliers,
        };
        if (!this.hasStoredSuppliers) {
          await this.storeFreightTableDraftSuppliers(payload);
        } else {
          await this.updateFreightTableDraftSuppliers(payload);
        }
        this.completedStep = Math.max(this.completedStep, 2);
      }
    },
    async onPrimaryAction() {
      if (!this.focusedSection || !this.focusedSectionValid) return;
      if (this.focusedSection === LAST_STEP) {
        this.onSave();
        return;
      }

      const step = this.focusedSection;
      this.saving = true;
      try {
        await this.persistStep();
        this.goToStep(step + 1);
      } catch (error) {
        handleApiError(this, error, STEP_SAVE_ERROR[step]);
      } finally {
        this.saving = false;
      }
    },
    async onSaveDraft() {
      if (!this.canSaveDraft) return;

      const step = this.focusedSection;
      this.savingDraft = true;
      try {
        await this.persistStep();
        this.$notification.success({
          message: "Rascunho salvo",
          description:
            "Você pode retomar o cadastro pela listagem de tabelas de frete.",
        });
        this.$router.push("/tabela-frete");
      } catch (error) {
        handleApiError(this, error, STEP_SAVE_ERROR[step]);
      } finally {
        this.savingDraft = false;
      }
    },
    onCancel() {
      confirmDeleteDraft({
        descricao: this.form.identification.descricao,
        codigo: this.form.identification.codigo,
        onOk: () => this.discardAndLeave(),
      });
    },
    async discardAndLeave() {
      this.discarding = true;
      try {
        if (this.tabfreId) {
          await this.deleteFreightTableDraft({ tabfreId: this.tabfreId });
          this.tabfreId = null;
        }
        this.$router.push("/tabela-frete");
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível descartar a tabela de frete",
        );
      } finally {
        this.discarding = false;
      }
    },
    async onSave() {
      if (!this.configRoutesDone || this.saving) return;
      this.saving = true;
      try {
        await this.saveFreightTableConfigRoutes({
          tabfreId: this.tabfreId,
          form: this.form.configRoutes,
          identification: this.form.identification,
          suppliers: this.form.suppliers,
        });
        await this.finalizeFreightTableDraft({ tabfreId: this.tabfreId });
        this.$notification.success({
          message: "Tabela de frete criada",
          description: "A tabela saiu de rascunho e já aparece na listagem.",
        });
        this.$router.replace(`/tabela-frete/${this.tabfreId}`);
      } catch (error) {
        // O store valida ~40 campos; sem abrir o errors bag o operador só vê
        // "Erro na validação dos dados" e não tem como saber qual campo caiu.
        const detalhe = describeStoreValidationErrors(error);
        if (detalhe) {
          this.$notification.error({
            message: "Não foi possível criar a tabela de frete",
            description: detalhe,
            duration: 0,
          });
        } else {
          handleApiError(this, error, "Não foi possível criar a tabela de frete");
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

@freight-footer-reserve: 100px;


.freight-table-create {
  font-family: @font-primary;
  padding: 24px 32px @freight-footer-reserve;

  &__title {
    font-family: @font-primary;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
    margin: 0 0 24px;
  }

  &__placeholder {
    color: @color-text-light;
    margin: 0;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 64px 0;
  }

  // Transportadora e Cliente lado a lado; sem cliente, a transportadora ocupa a linha.
  &__partners {
    display: flex;
    gap: 12px;

    > * {
      flex: 1 1 0;
      min-width: 0;
    }
  }

  &__actions-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 56px;
    background: #ffffff;
    box-shadow: 0px -2px 16px rgba(0, 0, 0, 0.16);
  }

  &__actions-group {
    display: flex;
    gap: 8px;
  }

  &__cancel {
    color: @color-danger-action;
    border-color: @color-danger-action;

    &:hover,
    &:focus {
      color: darken(@color-danger-action, 8%);
      border-color: darken(@color-danger-action, 8%);
    }
  }

  &__save {
    background-color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }
}
</style>
